import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const lancerResourceBarVariants = cva(
  "relative h-3 w-full overflow-hidden rounded-[2px] border bg-muted/30 shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]",
  {
    variants: {
      variant: {
        default: "",
        segmented:
          "[background-image:linear-gradient(to_right,transparent_0,transparent_calc(100%/var(--segments)-1px),hsl(var(--background)/0.6)_calc(100%/var(--segments)-1px),hsl(var(--background)/0.6)_calc(100%/var(--segments)))] [background-size:calc(100%/var(--segments))_100%]",
      },
      tone: {
        default: "border-border/70",
        success: "border-success/40",
        warning: "border-warning/50",
        destructive: "border-destructive/50",
        info: "border-info/50",
        accent: "border-accent/50",
      },
    },
    defaultVariants: {
      variant: "segmented",
      tone: "default",
    },
  },
);

interface LancerResourceBarProps
  extends Omit<React.ComponentProps<"div">, "children">,
    VariantProps<typeof lancerResourceBarVariants> {
  value: number;
  max?: number;
  segments?: number;
  indicatorClassName?: string;
  label?: React.ReactNode;
  showValue?: boolean;
}

function LancerResourceBar({
  className,
  value = 0,
  max = 100,
  segments = 10,
  variant = "segmented",
  tone = "default",
  indicatorClassName,
  label,
  showValue = true,
  ...props
}: LancerResourceBarProps) {
  const numericMax = Math.max(1, max);
  const numericValue = Math.max(0, Math.min(numericMax, value));
  const percentage = (numericValue / numericMax) * 100;

  return (
    <div
      data-slot="lancer-resource-bar"
      className={cn("grid gap-1.5", className)}
      {...props}
    >
      {(label || showValue) && (
        <div className="flex min-w-0 items-center justify-between gap-2 text-xs leading-none">
          {label ? (
            <span
              data-slot="lancer-resource-bar-label"
              className="truncate font-mono font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {label}
            </span>
          ) : (
            <span />
          )}
          {showValue ? (
            <span
              data-slot="lancer-resource-bar-value"
              className="font-mono font-semibold text-foreground"
            >
              {numericValue}/{numericMax}
            </span>
          ) : null}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={numericMax}
        aria-valuenow={numericValue}
        className={cn(lancerResourceBarVariants({ variant, tone }))}
        style={
          {
            "--segments": segments,
          } as React.CSSProperties
        }
      >
        <div
          data-slot="lancer-resource-bar-indicator"
          className={cn(
            "h-full transition-all duration-300 ease-out",
            tone === "default" && "bg-primary",
            tone === "success" && "bg-success",
            tone === "warning" && "bg-warning",
            tone === "destructive" && "bg-destructive",
            tone === "info" && "bg-info",
            tone === "accent" && "bg-accent",
            "shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]",
            indicatorClassName,
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export type { LancerResourceBarProps };
export { LancerResourceBar, lancerResourceBarVariants };
